import { MENU_ITEM_KEYBOARD_FOCUS_CLASS } from "@/components/Menu/menuStyles";
import { useCallback, useLayoutEffect, useState, type RefObject } from "react";

const GO_NAV_FOCUSED_SELECTOR = `[data-go-nav-item].${MENU_ITEM_KEYBOARD_FOCUS_CLASS}`;

export type GoNavGhostRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export default function useGoNavSlidingGhost(rootRef: RefObject<HTMLElement | null>, active: boolean) {
  const [ghostRect, setGhostRect] = useState<GoNavGhostRect | null>(null);

  const updateGhostRect = useCallback(() => {
    const root = rootRef.current;

    if (!root || !active) {
      setGhostRect(null);
      return;
    }

    const focused = root.querySelector<HTMLElement>(GO_NAV_FOCUSED_SELECTOR);

    if (!focused) {
      setGhostRect(null);
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const itemRect = focused.getBoundingClientRect();

    setGhostRect({
      left: itemRect.left - rootRect.left,
      top: itemRect.top - rootRect.top,
      width: itemRect.width,
      height: itemRect.height
    });
  }, [active, rootRef]);

  useLayoutEffect(() => {
    if (!active) {
      setGhostRect(null);
      return;
    }

    const frame = requestAnimationFrame(() => {
      updateGhostRect();
    });

    const root = rootRef.current;

    if (!root) {
      cancelAnimationFrame(frame);
      return;
    }

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;

      if (!(target instanceof HTMLElement) || !target.matches("[data-go-nav-item]")) {
        return;
      }

      updateGhostRect();
    };

    root.addEventListener("focusin", handleFocusIn);
    window.addEventListener("resize", updateGhostRect);

    const resizeObserver = new ResizeObserver(() => {
      updateGhostRect();
    });

    resizeObserver.observe(root);

    const mutationObserver = new MutationObserver(() => {
      updateGhostRect();
    });

    for (const item of root.querySelectorAll<HTMLElement>("[data-go-nav-item]")) {
      resizeObserver.observe(item);
      mutationObserver.observe(item, { attributes: true, attributeFilter: ["class"] });
    }

    updateGhostRect();

    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("resize", updateGhostRect);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [active, rootRef, updateGhostRect]);

  return ghostRect;
}
